import React, { useState } from 'react';
import { useApp } from '../../../contexts/AppContext';
import { useData } from '../../../hooks/useData';
import { TABLES } from '../../../constants';
import { MODAL_TYPES } from '../../../constants/modalTypes';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../../../utils/errorLogging';
import './BOQSetup.css';

/**
 * BOQ Setup component that allows selection and management of BOQ template items.
 *
 * Features:
 * - Groups items by category
 * - Supports draft saving
 * - Bulk import functionality
 * - Persists selections across sessions
 */
const BOQSetup = () => {
  const { currentProject, openModal, userProfile } = useApp();

  // Get BOQ categories
  const { data: boqCategories } = useData(TABLES.REFERENCE_BOQ_CATEGORIES, {
    select: '*',
    match: { is_active: true },
    orderBy: { column: 'display_order', ascending: true },
  });

  // Get template items with their categories
  const { data: templateItems } = useData(TABLES.MASTER_BOQ_TEMPLATE_ITEMS, {
    match: { created_by: userProfile?.id },
    select: `
      id,
      name,
      category_id,
      display_order,
      description,
      created_by,
      reference_boq_categories!inner(
        id,
        name,
        category_number
      )
    `,
    orderBy: [
      { column: 'reference_boq_categories.category_number', ascending: true },
      { column: 'display_order', ascending: true },
    ],
    realtime: false,
  });

  // Get draft items
  const {
    data: draftItems,
    remove: removeDraft,
    isLoading: isDraftLoading,
  } = useData(TABLES.DRAFT_PROJECT_BOQ_ITEMS, {
    select: '*',
    match: { project_id: currentProject?.id },
  });

  // Group drafts by template
  const draftsByTemplate = React.useMemo(() => {
    if (!draftItems) return {};
    return draftItems.reduce((acc, draft) => {
      acc[draft.template_id] = draft;
      return acc;
    }, {});
  }, [draftItems]);

  const handleOpenTemplateModal = templateId => {
    const template = templateItems?.find(t => t.id === templateId);
    if (!template) return;

    openModal(MODAL_TYPES.BOQ_TEMPLATE_SELECTION, {
      categoryId: template.category_id,
      templateId,
      onSubmit: () => {
        // Will be handled by useData's realtime subscription
      },
    });
  };

  const { logError } = useErrorLogging();
  const [isClearing, setIsClearing] = useState(false);

  const handleClearTemplate = async templateId => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את הטיוטה?')) {
      return;
    }

    const draft = draftsByTemplate[templateId];
    if (!draft) return;

    try {
      setIsClearing(true);
      await removeDraft(draft.id);
    } catch (error) {
      logError('Failed to clear draft', {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATABASE,
        error,
      });
    } finally {
      setIsClearing(false);
    }
  };

  const handleImportAll = () => {
    openModal(MODAL_TYPES.BOQ_TEMPLATE_IMPORT, {
      projectId: currentProject?.id,
      boqCategories,
      onSubmit: () => {
        // Will be handled by useData's realtime subscription
      },
    });
  };

  const isLoading = !templateItems || isDraftLoading;
  if (isLoading) {
    return <div className="section-content">טוען...</div>;
  }

  return (
    <div className="section-content">
      <div className="boq-section">
        <h2 className="boq-section-title">
          תבניות זמינות
          <div className="boq-section-subtitle">
            בחר תבנית כדי להוסיף את הפריטים שלה לכתב הכמויות
          </div>
        </h2>
        <div className="boq-categories-grid">
          {templateItems?.map(template => {
            const draft = draftsByTemplate[template.id];
            const selectedCount = Object.keys(draft?.selected_entries || {}).length;
            const category = template.reference_boq_categories;
            return (
              <div key={template.id} className="boq-category-card">
                <div className="card-header">
                  <h3>{template.name}</h3>
                  <div className="template-count">
                    {selectedCount > 0
                      ? `${selectedCount} פריטים נבחרו`
                      : `${template.description?.length || 0} פריטים זמינים`}
                  </div>
                </div>
                <div className="card-category">
                  <span className="category-number">{category.category_number}</span>
                  <span className="category-name">{category.name}</span>
                </div>
                <div className="card-actions">
                  <button
                    className={`ktav-button primary ${selectedCount > 0 ? 'selected' : ''}`}
                    onClick={() => handleOpenTemplateModal(template.id)}
                  >
                    {draft ? `${selectedCount} פריטים נבחרו` : 'בחר פריטים'}
                  </button>
                  {draft && (
                    <button
                      className="ktav-button danger"
                      onClick={() => handleClearTemplate(template.id)}
                      disabled={isClearing}
                      data-tooltip="נקה את כל הפריטים שנבחרו"
                      aria-label="נקה את כל הפריטים שנבחרו"
                    >
                      {isClearing ? '...' : '✕'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="section-actions">
        <button
          className="ktav-button primary"
          disabled={!Object.keys(draftsByTemplate).length}
          onClick={handleImportAll}
        >
          ייבא את כל הפריטים שנבחרו
        </button>
      </div>
    </div>
  );
};

export default BOQSetup;
